import {formatNumber} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {adbPct} from '@features/admin-panel/helpers/admin-dashboard-format';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {AdminDashboardReportBlock, AdminDashboardReportProvider, AdminDashboardReportRow} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {
  AdminDashboardRevenueCoverage,
  AdminDashboardRevenueCoverageRow
} from '@features/admin-panel/models/admin-dashboard-licences.models';
import {
  AdminDashboardQueryParams,
  AdminDashboardScope,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';

/** The card's title, on screen and in the PDF report (OCA-E-159 DetailSections:365). */
const TITLE = 'Revenue coverage';
/** The list shows this many companies until "Show all" (OCA-E-159 DetailSections:286). */
const SHOWN = 5;
/** The description before the first response, when nobody is paying and when the query failed. */
const NO_DESCRIPTION = 'How many paying companies have a Client Manager record.';
/** The note when every paying company is covered (OCA-E-159 DetailSections:378). */
const ALL_COVERED = 'Every paying company in this view has a Client Manager record.';

/**
 * Revenue coverage (ITP-E-160-09, Q5 / Q6): how many paying companies — users holding a solution package on the
 * latest nightly snapshot — have a Client Manager record and so are inside every revenue figure on the page, as a
 * sentence and a meter, then the paying companies that do not (most active users first, five until "Show all"), each
 * a Company 360 link. Shown in the Clients scope only. Reloads whenever the dashboard's query params change. With no
 * paying company it has nothing to show and the dashboard hides it; a failed query says so (ITP-E-173-01).
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-revenue-coverage',
  templateUrl: './admin-dashboard-revenue-coverage.component.html',
  styleUrls: ['./admin-dashboard-revenue-coverage.component.scss']
})
export class AdminDashboardRevenueCoverageComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  @Input() scope: AdminDashboardScope = 'Clients';
  @Input() params: AdminDashboardQueryParams;
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  readonly title = TITLE;
  readonly failedTitle = 'Couldn\'t load revenue coverage';
  coverage: AdminDashboardRevenueCoverage | null = null;
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;
  /** "Show all" was clicked: every row the server sent is listed. */
  showAll = false;

  private loadSubscription: Subscription;

  constructor(
    private dataService: AdminDashboardDataService,
    private router: Router,
    private reportRegistry: AdminDashboardReportRegistry,
    private host: ElementRef<HTMLElement>
  ) {}

  /** Every uncovered company the server sent (at most 200). */
  get rows(): AdminDashboardRevenueCoverageRow[] {
    return Array.isArray(this.coverage?.rows) ? this.coverage.rows : [];
  }

  /** The rows on screen: five, or all after "Show all". */
  get listed(): AdminDashboardRevenueCoverageRow[] {
    return this.showAll ? this.rows : this.rows.slice(0, SHOWN);
  }

  /** More uncovered companies than the five shown: the "Show all" link appears. */
  get hasMore(): boolean {
    return (this.coverage?.total ?? 0) > SHOWN;
  }

  /** "Show all 22" / "Show fewer". */
  get toggleText(): string {
    return this.showAll ? 'Show fewer' : `Show all ${formatNumber(this.coverage?.total ?? 0, 'en-US', '1.0-0')}`;
  }

  /** "1,182": the covered count, bold in the sentence. */
  get coveredText(): string {
    return formatNumber(this.coverage?.covered ?? 0, 'en-US', '1.0-0');
  }

  /** "1,204 paying companies" / "1 paying company". */
  get payingText(): string {
    return countOf(this.coverage?.paying ?? 0, 'paying company', 'paying companies');
  }

  /** The description as text (the template bolds the covered count; the PDF uses this). */
  get description(): string {
    if (!this.coverage?.paying) {
      return NO_DESCRIPTION;
    }
    return `Revenue covers ${this.coveredText} of ${this.payingText}. `
      + 'The rest have no Client Manager record, so no figure on this page includes them.';
  }

  /** The meter's fill in % of the track: covered / paying. */
  get coveredShare(): number {
    const paying = this.coverage?.paying ?? 0;
    return paying ? ((this.coverage.covered ?? 0) / paying) * 100 : 0;
  }

  /** "1,182 of 1,204 paying companies have a Client Manager record" (OCA-E-159 DetailSections:372). */
  get meterLabel(): string {
    return `${this.coveredText} of ${this.payingText} ${this.coverage?.covered === 1 ? 'has' : 'have'} a Client Manager record`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('licences-revenue', this.host.nativeElement, this);
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.reportRegistry.unregister(this);
  }

  toggle(): void {
    this.showAll = !this.showAll;
  }

  /** A company name: its Company 360 page. */
  open360(companyId: string): void {
    this.router.navigateByUrl('/admin-panel/company-360/' + companyId);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-10). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    const title = TITLE;
    if (this.failed) {
      return [{kind: 'empty', title, text: this.failedTitle}];
    }
    const subtitle = this.description;
    const coverage = this.coverage;
    if (!coverage) {
      return [{kind: 'empty', title, subtitle, text: 'Coverage is not available'}];
    }
    if (!coverage.paying) {
      return [{kind: 'empty', title, subtitle, text: 'No paying companies in this view'}];
    }
    const stats: AdminDashboardReportBlock = {
      kind: 'stats',
      title,
      subtitle,
      stats: [{
        label: 'Covered',
        value: `${this.coveredText} of ${formatNumber(coverage.paying, 'en-US', '1.0-0')}`,
        detail: adbPct(coverage.covered / coverage.paying)
      }]
    };
    if (!this.rows.length) {
      return [{...stats, note: ALL_COVERED}];
    }
    const table: AdminDashboardReportBlock = {
      kind: 'table',
      title: 'Paying companies with no Client Manager record',
      subtitle: countOf(coverage.total, 'company', 'companies'),
      columns: [{header: 'Company', weight: 3}, {header: 'Active users', align: 'right'}],
      rows: this.rows.map((row): AdminDashboardReportRow => ({
        cells: [row.companyName, formatNumber(row.activeUsers ?? 0, 'en-US', '1.0-0')]
      }))
    };
    if (coverage.total > this.rows.length) {
      table.note = `Showing ${formatNumber(this.rows.length, 'en-US', '1.0-0')} of ${formatNumber(coverage.total, 'en-US', '1.0-0')}.`;
    }
    return [stats, table];
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getData<AdminDashboardRevenueCoverage>(
      'AdminDashboardRevenueCoverageQuery', this.params, 'Coverage'
    ).subscribe({
      next: coverage => this.show(coverage ?? null, false),
      error: () => this.show(null, true)
    });
  }

  /** "No paying companies in this view" has nothing to show: it reports empty. */
  private show(coverage: AdminDashboardRevenueCoverage | null, failed: boolean): void {
    this.coverage = coverage;
    this.failed = failed;
    this.showAll = false;
    this.loaded = true;
    this.report(failed ? 'failed' : coverage?.paying ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }
}
